import { useCallback, useEffect } from 'react';
import { type Path, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';

import { DatePickerController } from '@/components/form/controllers/date-picker-controller';
import { cn } from '@/lib/utils';

import type { EstatesFiltersSchemaType } from '../schemas';
import {
  activeFilterFieldClassName,
  activeFilterInputClassName,
  hasActiveFilterValue,
} from './estate-filter-active-field';

export const EstateFilterDates = () => {
  const { t } = useTranslation();

  const methods = useFormContext<EstatesFiltersSchemaType>();
  const { setError, clearErrors, watch } = methods;

  const createdAt = watch(['createdAt.min', 'createdAt.max']);
  const updatedAt = watch(['updatedAt.min', 'updatedAt.max']);
  const createdAtMin = watch('createdAt.min');
  const createdAtMax = watch('createdAt.max');
  const updatedAtMin = watch('updatedAt.min');
  const updatedAtMax = watch('updatedAt.max');

  const validateDateRange = useCallback(
    ([from, to]: Array<Date | string | undefined>, fields: Path<EstatesFiltersSchemaType>[]) => {
      if (from && to) {
        const isValid = dayjs(from).isBefore(dayjs(to)) || dayjs(from).isSame(dayjs(to));
        if (!isValid) {
          fields.forEach((field) => {
            setError(field, {
              type: 'manual',
              message: 'invalid date range',
            });
          });
        } else {
          fields.forEach((field) => {
            clearErrors(field);
          });
        }
      }
      return true;
    },
    [setError, clearErrors]
  );

  useEffect(() => {
    validateDateRange(createdAt, ['createdAt.min', 'createdAt.max']);
  }, [validateDateRange, createdAt]);

  useEffect(() => {
    validateDateRange(updatedAt, ['updatedAt.min', 'updatedAt.max']);
  }, [validateDateRange, updatedAt]);

  return (
    <fieldset className="mb-5 space-y-6">
      <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
      <div>
        <p className="mb-2 text-sm font-semibold text-primary-dark">
          {t('createdDate', 'Created Date')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <DatePickerController
            methods={methods}
            name="createdAt.min"
            label={t('from', 'From')}
            wrapperClassName={cn(hasActiveFilterValue(createdAtMin) && activeFilterFieldClassName)}
            className={cn(hasActiveFilterValue(createdAtMin) && activeFilterInputClassName)}
          />
          <DatePickerController
            methods={methods}
            name="createdAt.max"
            label={t('to', 'To')}
            wrapperClassName={cn(hasActiveFilterValue(createdAtMax) && activeFilterFieldClassName)}
            className={cn(hasActiveFilterValue(createdAtMax) && activeFilterInputClassName)}
          />
        </div>
      </div>

      <div className="border-t border-background-lighter" />

      <div>
        <p className="mb-2 text-sm font-semibold text-primary-dark">
          {t('updatedDate', 'Updated Date')}
        </p>
        <div className="grid grid-cols-2 gap-4">
          <DatePickerController
            methods={methods}
            name="updatedAt.min"
            label={t('from', 'From')}
            wrapperClassName={cn(hasActiveFilterValue(updatedAtMin) && activeFilterFieldClassName)}
            className={cn(hasActiveFilterValue(updatedAtMin) && activeFilterInputClassName)}
          />
          <DatePickerController
            methods={methods}
            name="updatedAt.max"
            label={t('to', 'To')}
            wrapperClassName={cn(hasActiveFilterValue(updatedAtMax) && activeFilterFieldClassName)}
            className={cn(hasActiveFilterValue(updatedAtMax) && activeFilterInputClassName)}
          />
        </div>
      </div>
      <div className="border-t border-[hsl(214.3_31.8%_91.4%)]" />
    </fieldset>
  );
};
